import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CreditCard, FileText, Megaphone, MessageSquare, Plane, Plus, Trash2, TrendingUp, Wallet } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useDemo } from "@/store/DemoStore";
import { Card, CardTitle, Countdown, btn } from "@/components/common";
import { TripRow } from "@/components/TripCard";
import { daysUntil, fmtDate, fmtMoney, nextPayment, tripCommission, tripTotal } from "@/lib/trip";

const PRIORITY = {
  low: ["Low", "bg-slate-100 text-slate-600", "border-l-slate-300"],
  medium: ["Medium", "bg-amber-50 text-amber-700", "border-l-amber-400"],
  high: ["High", "bg-red-50 text-red-700", "border-l-red-500"],
};
const PRANK = { high: 0, medium: 1, low: 2 };
const annField = "w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-base text-navy-900 outline-none focus:border-navy-500 focus:ring-2 focus:ring-navy-100 sm:text-sm";

const greet = () => { const h = new Date().getHours(); return h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening"; };

function Kpi({ icon: Icon, label, value, hint, id }) {
  return (
    <Card className="p-5" data-testid={`kpi-${id}`}>
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium text-slate-500">{label}</p>
        <span className="grid h-8 w-8 place-items-center rounded-lg bg-navy-50 text-navy-700"><Icon className="h-4 w-4" /></span>
      </div>
      <p className="mt-3 font-display text-2xl font-bold tracking-tight text-navy-900 sm:text-[28px]">{value}</p>
      <p className="mt-1 text-xs text-slate-500">{hint}</p>
    </Card>
  );
}

function Hero({ name }) {
  const [q, setQ] = useState("");
  const nav = useNavigate();
  return (
    <div className="relative overflow-hidden rounded-3xl bg-navy-900 px-6 py-8 sm:px-10 sm:py-10">
      <img src="/images/sunset.jpg" alt="" className="absolute inset-y-0 right-0 hidden h-full w-1/2 object-cover opacity-60 md:block" />
      <div className="absolute inset-y-0 right-0 hidden w-1/2 bg-gradient-to-r from-navy-900 via-navy-900/40 to-transparent md:block" />
      <div className="relative max-w-xl">
        <p className="text-xs font-medium uppercase tracking-[0.18em] text-white/50">{new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}</p>
        <h1 className="mt-2 font-display text-3xl font-bold tracking-tight text-white sm:text-4xl" data-testid="dashboard-greeting">{greet()}, {name.split(" ")[0]}.</h1>
        <p className="mt-2 text-sm text-white/60">Here's what's moving across your book of business today.</p>
        <form onSubmit={(e) => { e.preventDefault(); nav(`/agent/trips?q=${encodeURIComponent(q)}`); }} className="mt-6 flex gap-2">
          <input data-testid="agent-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search trips, clients, booking numbers…" className="h-11 min-w-0 flex-1 rounded-xl border border-white/10 bg-white/10 px-4 text-base text-white outline-none backdrop-blur placeholder:text-white/40 focus:border-white/30 sm:text-sm" />
          <button data-testid="agent-search-submit" className="h-11 shrink-0 rounded-xl bg-white px-4 text-sm font-semibold text-navy-900 hover:bg-slate-100">Search</button>
        </form>
      </div>
    </div>
  );
}

function Attention({ items }) {
  return (
    <Card data-testid="attention-card">
      <CardTitle action={<span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700">{items.length}</span>}>Needs attention</CardTitle>
      <ul className="max-h-[22rem] overflow-y-auto p-3">
        {items.map((i) => (
          <li key={i.key}>
            <Link to={i.to} data-testid={`attention-${i.key}`} className="flex items-start gap-3 rounded-xl px-3 py-3 transition-colors hover:bg-slate-50">
              <span className={`mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-lg ${i.tone}`}><i.icon className="h-4 w-4" /></span>
              <div className="min-w-0">
                <p className="text-sm font-medium text-navy-900">{i.title}</p>
                <p className="text-xs text-slate-500">{i.text}</p>
              </div>
            </Link>
          </li>
        ))}
        {!items.length && <p className="px-3 py-6 text-sm text-slate-500">All clear.</p>}
      </ul>
    </Card>
  );
}

function NewAnnouncementDialog({ open, onOpenChange }) {
  const { createAnnouncement } = useDemo();
  const [f, setF] = useState({ title: "", text: "", priority: "medium" });
  const submit = async (e) => {
    e.preventDefault();
    const r = await createAnnouncement(f);
    if (r) { onOpenChange(false); setF({ title: "", text: "", priority: "medium" }); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-1.5rem)] max-w-lg rounded-2xl p-5 sm:p-7" data-testid="new-announcement-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-navy-900">New Announcement</DialogTitle>
          <DialogDescription>Posted to the dashboard for the whole agency.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <label className="block"><span className="mb-1.5 block text-xs font-medium text-slate-500">Priority</span>
            <select className={annField} value={f.priority} onChange={(e) => setF({ ...f, priority: e.target.value })} data-testid="announcement-priority-select">
              {Object.entries(PRIORITY).map(([k, v]) => <option key={k} value={k}>{v[0]}</option>)}
            </select></label>
          <label className="block"><span className="mb-1.5 block text-xs font-medium text-slate-500">Title</span>
            <input required maxLength={120} className={annField} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="announcement-title-input" /></label>
          <label className="block"><span className="mb-1.5 block text-xs font-medium text-slate-500">Message</span>
            <textarea required rows={3} maxLength={2000} className={annField} value={f.text} onChange={(e) => setF({ ...f, text: e.target.value })} data-testid="announcement-text-input" /></label>
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={() => onOpenChange(false)} className={btn.secondary}>Cancel</button>
            <button className={btn.primary} data-testid="announcement-submit">Post Announcement</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Announcements() {
  const { announcements = [], isAdmin, deleteAnnouncement } = useDemo();
  const [open, setOpen] = useState(false);
  const list = [...announcements].sort((a, b) => (PRANK[a.priority] ?? 1) - (PRANK[b.priority] ?? 1) || b.created_at.localeCompare(a.created_at));
  return (
    <Card data-testid="announcements">
      <CardTitle action={isAdmin ? <button onClick={() => setOpen(true)} data-testid="add-announcement-btn" className="grid h-8 w-8 place-items-center rounded-lg bg-navy-900 text-white transition-colors hover:bg-navy-800"><Plus className="h-4 w-4" /></button> : null}>Announcements</CardTitle>
      <ul className="grid grid-cols-1 gap-3 p-4 sm:p-5 md:grid-cols-2">
        {!list.length && <li className="px-1 py-4 text-sm text-slate-500">{isAdmin ? "No announcements yet. Tap + to post one." : "No announcements yet."}</li>}
        {list.map((a) => {
          const pr = PRIORITY[a.priority] || PRIORITY.medium;
          const canDelete = isAdmin;
          return (
            <li key={a.id} className={`flex gap-3 rounded-xl border border-l-4 border-slate-100 p-4 ${pr[2]}`} data-testid={`announcement-${a.id}`}>
              <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-navy-50 text-navy-700"><Megaphone className="h-4 w-4" /></span>
              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm font-semibold text-navy-900">{a.title}</p>
                    <span className={`rounded-md px-1.5 py-0.5 text-[10px] font-semibold ${pr[1]}`} data-testid={`announcement-priority-${a.id}`}>{pr[0]}</span>
                  </div>
                  {canDelete && <button onClick={() => deleteAnnouncement(a.id)} data-testid={`announcement-delete-${a.id}`} className="grid h-7 w-7 shrink-0 place-items-center rounded-lg text-slate-400 transition-colors hover:bg-red-50 hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>}
                </div>
                <p className="mt-1 text-sm leading-relaxed text-slate-500">{a.text}</p>
                <p className="mt-2 text-[11px] font-medium text-slate-400">{a.from} · {fmtDate(a.date, { month: "short", day: "numeric" })}</p>
              </div>
            </li>
          );
        })}
      </ul>
      <NewAnnouncementDialog open={open} onOpenChange={setOpen} />
    </Card>
  );
}

export default function AgentDashboard() {
  const { trips, conversations, settings, client, paidExtra } = useDemo();
  const booked = trips.filter((t) => t.status !== "QUOTE");
  const revenue = booked.reduce((s, t) => s + tripTotal(t), 0);
  const upcoming = trips.filter((t) => t.status === "BOOKED" && daysUntil(t.departure) >= 0).sort((a, b) => a.departure.localeCompare(b.departure));
  const quotes = trips.filter((t) => t.status === "QUOTE");

  const attention = [
    ...upcoming.map((t) => ({ t, p: nextPayment(t, paidExtra(t.id)) })).filter(({ p }) => {
      if (!p) return false;
      if (p.state === "overdue") return true;
      // Deposit stays on the 30-day window; final/other balances only surface within 5 days of the deadline.
      return /deposit/i.test(p.label) ? p.state === "due-soon" : daysUntil(p.due) <= 5;
    }).map(({ t, p }) => ({ key: `pay-${t.id}`, icon: CreditCard, tone: "bg-amber-50 text-amber-700", title: `${p.label} ${p.state === "overdue" ? "overdue" : "due"} · ${fmtMoney(p.outstanding ?? p.amount, true)}`, text: `${client(t.clientId).name} · due ${fmtDate(p.due)}`, to: `/agent/trips/${t.id}?tab=invoice` })),
    ...quotes.filter((t) => t.status === "QUOTE").map((t) => ({ key: `quote-${t.id}`, icon: FileText, tone: "bg-sky-50 text-sky-700", title: `Quote ${t.quoteNumber} awaiting approval`, text: `${client(t.clientId).name} · ${t.title}`, to: `/agent/quotes/${t.id}` })),
    ...conversations.filter((c) => c.unreadAgent > 0).map((c) => ({ key: `msg-${c.id}`, icon: MessageSquare, tone: "bg-navy-50 text-navy-700", title: `${c.unreadAgent} unread from ${client(c.clientId).name}`, text: c.messages[c.messages.length - 1]?.text, to: `/agent/messages/${c.id}` })),
  ];

  return (
    <div className="space-y-6 lg:space-y-8">
      <Hero name={settings.advisorName} />
      <div className="flex flex-wrap gap-2">
        <Link to="/agent/quotes?new=1" data-testid="dashboard-new-quote" className={btn.primary}><Plus className="h-4 w-4" />New Quote</Link>
        <Link to="/agent/clients" data-testid="dashboard-view-clients" className={btn.secondary}>View Clients</Link>
        <Link to="/agent/messages" data-testid="dashboard-open-messages" className={btn.secondary}>Open Inbox</Link>
      </div>
      <div className="grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-4">
        <Kpi id="revenue" icon={TrendingUp} label="Total Sales" value={fmtMoney(revenue)} hint="Booked & Completed Trips" />
        <Kpi id="commission" icon={Wallet} label="Total Commission" value={fmtMoney(booked.reduce((s, t) => s + tripCommission(t, settings.commissionRate), 0))} hint={`Agent Share ${settings.agentSplit}%`} />
        <Kpi id="bookings" icon={Plane} label="Total Bookings" value={booked.length} hint={`${upcoming.length} upcoming departures`} />
        <Kpi id="quotes" icon={FileText} label="Open Quotes" value={quotes.length} hint={`${fmtMoney(quotes.reduce((s, t) => s + tripTotal(t), 0))} in pipeline`} />
      </div>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="min-w-0 lg:col-span-2" data-testid="upcoming-departures">
          <CardTitle action={<Link to="/agent/trips" className="text-xs font-semibold text-navy-700 hover:underline">View All</Link>}>Upcoming Departures</CardTitle>
          <div className="p-2 sm:p-3">
            {!upcoming.length && <p className="px-3 py-6 text-sm text-slate-500">No upcoming departures.</p>}
            {upcoming.slice(0, 5).map((t) => (
              <TripRow key={t.id} trip={t} to={`/agent/trips/${t.id}`} sub={`${client(t.clientId).name} · ${t.vessel} · ${fmtDate(t.departure)}`} right={<Countdown date={t.departure} className="border border-slate-200 shadow-none" />} />
            ))}
          </div>
        </Card>
        <Attention items={attention} />
      </div>
      <Announcements />
    </div>
  );
}
